import {
  Flex,
  IconButton,
  Text,
  Tooltip,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useCallback, useMemo, useState } from "react";
import { showErrorToast, translateSubElementString } from "../../../../../../../utils";
import {
  useGetDownstreamTransportationAndDistribution
} from "../../../../../../../hooks/query/reports";
import { hideAppSpinner } from "../../../../../../../context/Store/slices/appSpinnerSlice";
import CategoryPreviewWrapper from "./CategoryPreviewWrapper";
import { NumberField } from "../../../../../../../components/NumberField";
import { DataTable } from "../../../../../../../components/DataTable";
import { FaExclamationCircle } from "react-icons/fa";
import { ReviewDataModal } from "../ReviewDataModal";
import { useAuth } from "../../../../../../../context";
import { ReportsApi } from "../../../../../../../api";
import { fetchAllPages, plainExportHeader } from "../../../../../../../utils/fetchAllPages";

const numberOrNull = (value) => (value === null || value === undefined || value === "" ? null : Number(value));

function DownstreamTransportationAndDistributionPreview({ currentElement, reportId }) {
  const labelKey = "transportationAndDistribution";
  const { t } = useTranslation("translation", {
    keyPrefix: `reportsPage.scopeThree.inputs.${labelKey}`
  });
  const { t: globalTranslation } = useTranslation("translation");
  const questionnaireKeyPrefix = `organizationQuestionnaire.scope3.inputs.${labelKey}`;
  const { t: questionnaireTranslation, i18n } = useTranslation("translation", {
    keyPrefix: questionnaireKeyPrefix
  });
  const dispatch = useDispatch();
  const toast = useToast();
  const {
    isOpen: isReviewModalOpen,
    onOpen: onReviewModalOpen,
    onClose: onReviewModalClose
  } = useDisclosure();
  const [selectedAttribute, setSelectedAttribute] = useState(null);
  const [reviewData, setReviewData] = useState(null);

  const [dataList, setDataList] = useState([]);

  const [dataTableState, setDataTableState] = useState({
    queryPageIndex: 0,
    queryPageSize: 10,
    // Items are already pre-sorted by the backend by "typeOfTransport", "subtypeOfTransport" and "subtypeTwo"
    queryPageSortBy: [],
    totalElements: 0,
    totalPages: 0
  });

  const { totalPages, queryPageIndex, queryPageSize, queryPageSortBy, totalElements } = dataTableState;

  const manageDataTableState = (newValObject) => {
    setDataTableState((prevState) => {
      return {
        ...prevState,
        ...newValObject
      };
    });
  };

  const columns = useMemo(() => {
      const handleReviewData = (attribute) => {
        onReviewModalOpen();
        setSelectedAttribute(attribute);
        if (attribute) {
          setReviewData(currentElement[`${attribute}Items`].map(el => {
            return {
              ...el,
              [attribute]: questionnaireTranslation(`${attribute}.options.${el[attribute].code}`, {
                defaultValue: el[attribute] ?? ""
              })
            };
          }));
        }
      };

      return [
        {
          header: <Flex alignItems="center">
            {t("gridColumns.typeOfTransport")}
            <Tooltip
              label={t("reviewData", { attribute: t("gridColumns.typeOfTransport") })}
              whiteSpace={"pre-line"}
              hasArrow
            >
              <IconButton
                aria-label="upstreamTransportationAndDistributionInfo"
                type="button"
                icon={<FaExclamationCircle size="20" />}
                flexShrink="0"
                size="sm"
                opacity="0.25"
                tabIndex={-1}
                onClick={() => {
                  handleReviewData("typeOfTransport");
                }}
              />
            </Tooltip>
          </Flex>,
          accessorKey: "typeOfTransport",
          meta: { exportHeader: t("gridColumns.typeOfTransport") },
          size: 230
        },
        {
          header: t("gridColumns.subtypeOfTransport"),
          accessorKey: "subtypeOfTransport",
          size: 220,
          customStyles: {
            verticalAlign: "middle"
          }
        },
        {
          header: t("gridColumns.subtypeTwo"),
          accessorKey: "subtypeTwo",
          size: 220,
          customStyles: {
            verticalAlign: "middle"
          }
        },
        {
          header: t("gridColumns.unit"),
          accessorKey: "unit",
          size: 100,
          customStyles: {
            verticalAlign: "middle"
          }
        },
        {
          header: t("gridColumns.value"),
          accessorKey: "value",
          size: 100,
          customStyles: {
            verticalAlign: "middle"
          }
        },
        {
          header: translateSubElementString(`reportsPage.scopeThree.inputs.${labelKey}.gridColumns.emissions`),
          accessorKey: "emissions",
          meta: { exportHeader: plainExportHeader(globalTranslation(`reportsPage.scopeThree.inputs.${labelKey}.gridColumns.emissions`)) },
          size: 150,
          customStyles: {
            verticalAlign: "middle"
          }
        }
      ];
    },
    [t, globalTranslation, onReviewModalOpen, questionnaireTranslation, currentElement]
  );

  // One mapping for the page on screen and for Export; the export keeps the numbers numeric so Excel can sum them.
  const toRow = useCallback(
    (item, { forExport = false } = {}) => ({
      id: item.id,
      typeOfTransport: i18n.exists(`${questionnaireKeyPrefix}.typeOfTransport.options.${item.typeOfTransport?.code ?? ""}`) ?
        questionnaireTranslation(`typeOfTransport.options.${item.typeOfTransport?.code}`) : item.typeOfTransport?.name ?? "",
      subtypeOfTransport: i18n.exists(`${questionnaireKeyPrefix}.subtypeOfTransport.options.${item.subtypeOfTransport?.code ?? ""}`) ?
        questionnaireTranslation(`subtypeOfTransport.options.${item.subtypeOfTransport?.code}`) : item.subtypeOfTransport?.name ?? "",
      subtypeTwo: i18n.exists(`${questionnaireKeyPrefix}.subtypeTwo.options.${item.subtypeTwo?.code ?? ""}`) ?
        questionnaireTranslation(`subtypeTwo.options.${item.subtypeTwo?.code}`) : item.subtypeTwo?.name ?? "",
      unit: i18n.exists(`${questionnaireKeyPrefix}.unit.options.${item.unit?.code ?? ""}`) ?
        questionnaireTranslation(`unit.options.${item.unit?.code}`) : item.unit?.name ?? "",
      value: forExport ? numberOrNull(item.value) : Number(item.value)?.toLocaleString("en-GB", { maximumFractionDigits: 4 }),
      emissions: forExport ? numberOrNull(item.co2Emissions) : Number(item.co2Emissions)?.toLocaleString("en-GB", { maximumFractionDigits: 4 })
    }),
    [i18n, questionnaireKeyPrefix, questionnaireTranslation]
  );

  const { callApi } = useAuth();
  // Every row, not the page on screen, in the same order the table shows.
  const fetchAllRowsForExport = () =>
    fetchAllPages(({ page, size }) =>
      callApi(ReportsApi.getDownstreamTransportationAndDistribution, { reportId, params: { page, size, sort: queryPageSortBy } })
    ).then((items) => items.map((item) => toRow(item, { forExport: true })));

  const {
    isLoading,
    isFetching
  } = useGetDownstreamTransportationAndDistribution(
    reportId,
    { page: queryPageIndex, size: queryPageSize, sort: queryPageSortBy },
    {
      onSuccess: (page) => {
        const pageContent = page?.content ?? [];
        const dataItems = pageContent.map((item) => toRow(item));

        setDataList(dataItems);
        if (pageContent.length === 0 && page?.number !== 0 && page?.totalElements !== 0 && queryPageIndex > 0) {
          // Case when page size is changed to larger and user has to be redirected to the previous page
          manageDataTableState({
            totalElements: page?.totalElements ?? 0,
            totalPages: page?.totalPages ?? 0,
            queryPageIndex: queryPageIndex - 1
          });
        } else {
          manageDataTableState({
            totalElements: page?.totalElements ?? 0,
            totalPages: page?.totalPages ?? 0
          });
        }
      },
      onError: err => showErrorToast(toast, err.message),
      onSettled: () => {
        dispatch(hideAppSpinner());
      },
      cacheTime: 0
    }
  );

  const handleReviewModalClose = () => {
    setSelectedAttribute(null);
    setReviewData(null);
    onReviewModalClose();
  };

  return (
    <CategoryPreviewWrapper>
      <Flex marginBlockEnd={4}>
        <Text size="sm" fontWeight="bold">
          {translateSubElementString(
            `reportsPage.scopeThree.inputs.mainTotalEmissions`
          )}
        </Text>
        <NumberField value={currentElement?.totalCo2Emissions} marginStart="2" />
      </Flex>
      <DataTable
        data={dataList}
        columns={columns}
        isLoading={isLoading || isFetching}
        containerHeight={dataList && dataList.length > 0 ? "370px" : null}
        marginBlockEnd={8}
        serverSidePagination={true}
        fetchAllRowsForExport={fetchAllRowsForExport}
        queryTotalCount={totalElements}
        queryPageCount={totalPages}
        queryPageIndex={queryPageIndex}
        setQueryPageIndex={(value) => manageDataTableState({ queryPageIndex: value })}
        queryPageSize={queryPageSize}
        setQueryPageSize={(value) => manageDataTableState({ queryPageSize: value })}
      />
      {
        isReviewModalOpen && selectedAttribute !== null && reviewData !== null && (
          <ReviewDataModal
            isOpen={isReviewModalOpen}
            onClose={handleReviewModalClose}
            dataAttribute={selectedAttribute}
            gridHeader={t(`gridColumns.${selectedAttribute}`)}
            data={reviewData}
          />
        )
      }
    </CategoryPreviewWrapper>
  );
}

export default DownstreamTransportationAndDistributionPreview;